(() => {
  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const preservePreviewParams = (url) => {
    const resolved = new URL(url, window.location.href);
    const current = new URL(window.location.href);
    ['preview_theme_id', '_ab', '_fd', '_sc', 'pb'].forEach((key) => {
      if (current.searchParams.has(key)) resolved.searchParams.set(key, current.searchParams.get(key));
    });
    return resolved.toString();
  };

  const locationLabel = (venue) => [venue.city, venue.state].filter(Boolean).join(', ') || venue.country || '';

  const cityKey = (venue) =>
    [venue.city, venue.state, venue.country].map(normalize).join('|');

  const compareName = (a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { numeric: true, sensitivity: 'base' });

  const initDiscovery = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const views = Array.from(root.querySelectorAll('[data-venue-view]'));
    const search = root.querySelector('[data-venue-discovery-search]');
    const searchClear = root.querySelector('[data-venue-search-clear]');
    const searchStatus = root.querySelector('[data-venue-discovery-status]');
    const searchResults = root.querySelector('[data-venue-discovery-results]');
    const cityGroup = root.querySelector('[data-city-results-group]');
    const cityResults = root.querySelector('[data-city-results]');
    const venueGroup = root.querySelector('[data-venue-results-group]');
    const venueResults = root.querySelector('[data-venue-results]');
    const citySearchMore = root.querySelector('[data-city-search-more]');
    const venueSearchMore = root.querySelector('[data-venue-search-more]');
    const selectedCityName = root.querySelector('[data-selected-city-name]');
    const citySearch = root.querySelector('[data-venue-city-search]');
    const cityClear = root.querySelector('[data-venue-city-clear]');
    const cityList = root.querySelector('[data-city-venue-list]');
    const cityStatus = root.querySelector('[data-city-venue-status]');
    const selectedVenueContent = root.querySelector('[data-selected-venue-content]');
    const backCityName = root.querySelector('[data-back-city-name]');
    const startExtras = root.querySelector('[data-venue-start-extras]');
    const popularCityButtons = Array.from(root.querySelectorAll('[data-popular-city]'));
    const browseAllButton = root.querySelector('[data-browse-all-venues]');
    const browseList = root.querySelector('[data-browse-list]');
    const browseStatus = root.querySelector('[data-browse-status]');
    const browsePagination = root.querySelector('[data-browse-pagination]');
    const browsePageStatus = root.querySelector('[data-browse-page-status]');
    const browsePrevious = root.querySelector('[data-browse-previous]');
    const browseNext = root.querySelector('[data-browse-next]');

    if (!search || !searchResults || !cityResults || !citySearchMore || !venueResults || !venueSearchMore || !citySearch || !cityList || !selectedVenueContent) return;

    const venues = [];
    const handles = new Set();
    let allLoaded = false;
    let loadingPromise = null;
    let selectedCity = null;
    let selectedVenue = null;
    let detailRequest = 0;
    let searchCityLimit = 6;
    let searchVenueLimit = 8;
    let browsePage = 1;
    let venueReturnView = 'start';
    const browsePageSize = 25;
    let inputTimer;

    const parseIndex = (container) => {
      const script = container?.querySelector('[data-venue-discovery-index]');
      if (!script) return;
      let records = [];
      try {
        records = JSON.parse(script.textContent);
      } catch (error) {
        return;
      }
      records.forEach((venue) => {
        if (!venue?.handle || handles.has(venue.handle)) return;
        handles.add(venue.handle);
        venues.push(venue);
      });
    };

    parseIndex(root);

    const fetchRoot = async (url) => {
      const response = await fetch(preservePreviewParams(url), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('The venue index could not be loaded.');
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      return documentFragment.getElementById(root.id);
    };

    const ensureIndex = () => {
      if (allLoaded) return Promise.resolve();
      if (loadingPromise) return loadingPromise;

      loadingPromise = (async () => {
        const firstNextUrl = root.dataset.nextIndexUrl;
        if (!firstNextUrl) {
          allLoaded = true;
          return;
        }

        const firstPageUrl = new URL(firstNextUrl, window.location.href);
        const pageParameter = Array.from(firstPageUrl.searchParams.keys()).find((key) => key.startsWith('page_'));
        const totalPages = Math.ceil(Number(root.dataset.totalVenues || venues.length) / 50);

        if (pageParameter && totalPages > 1 && totalPages <= 20) {
          const pageUrls = [];
          for (let page = 2; page <= totalPages; page += 1) {
            const pageUrl = new URL(firstPageUrl);
            pageUrl.searchParams.set(pageParameter, String(page));
            pageUrls.push(pageUrl.toString());
          }
          const pageRoots = await Promise.all(pageUrls.map(fetchRoot));
          pageRoots.forEach(parseIndex);
        } else {
          let nextUrl = firstNextUrl;
          let guard = 0;
          while (nextUrl && guard < 20) {
            guard += 1;
            const nextRoot = await fetchRoot(nextUrl);
            if (!nextRoot) break;
            parseIndex(nextRoot);
            nextUrl = nextRoot.dataset.nextIndexUrl;
          }
        }

        allLoaded = true;
      })().finally(() => {
        loadingPromise = null;
      });

      return loadingPromise;
    };

    const showView = (name) => {
      views.forEach((view) => {
        view.hidden = view.dataset.venueView !== name;
      });
      root.dataset.activeView = name;
      const heading = root.querySelector('[data-venue-view="' + name + '"] h2');
      if (heading && name !== 'start') heading.focus?.({ preventScroll: true });
    };

    const makeImage = (venue) => {
      const media = document.createElement('span');
      media.className = 'venue-discovery__row-media';
      if (venue.image) {
        const image = document.createElement('img');
        image.src = venue.image;
        image.alt = venue.imageAlt || venue.name;
        image.loading = 'lazy';
        image.width = 96;
        image.height = 72;
        media.appendChild(image);
      } else {
        media.classList.add('venue-discovery__row-media--empty');
        media.setAttribute('aria-hidden', 'true');
      }
      return media;
    };

    const makeVenueButton = (venue) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'venue-discovery__result-row venue-discovery__result-row--venue';
      button.dataset.selectVenue = venue.handle;
      button.appendChild(makeImage(venue));

      const copy = document.createElement('span');
      copy.className = 'venue-discovery__row-copy';
      const name = document.createElement('strong');
      name.textContent = venue.name;
      const location = document.createElement('span');
      location.textContent = locationLabel(venue);
      copy.append(name, location);

      const arrow = document.createElement('span');
      arrow.className = 'venue-discovery__row-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      button.append(copy, arrow);
      return button;
    };

    const makeCityButton = (city) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'venue-discovery__result-row venue-discovery__result-row--city';
      button.dataset.selectCity = city.key;

      const pin = document.createElement('span');
      pin.className = 'venue-discovery__pin';
      pin.setAttribute('aria-hidden', 'true');

      const copy = document.createElement('span');
      copy.className = 'venue-discovery__row-copy';
      const name = document.createElement('strong');
      name.textContent = city.city;
      const region = document.createElement('span');
      region.textContent = [city.state, city.country].filter(Boolean).join(', ');
      copy.append(name, region);

      const arrow = document.createElement('span');
      arrow.className = 'venue-discovery__row-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      button.append(pin, copy, arrow);
      return button;
    };

    const matchingCities = (query) => {
      const byKey = new Map();
      venues.forEach((venue) => {
        if (!venue.city || !normalize(venue.city).includes(query)) return;
        const key = cityKey(venue);
        if (!byKey.has(key)) {
          byKey.set(key, {
            key,
            city: venue.city,
            state: venue.state,
            country: venue.country
          });
        }
      });
      return Array.from(byKey.values())
        .sort((a, b) => String(a.city).localeCompare(String(b.city), undefined, { sensitivity: 'base' }));
    };

    const matchingVenues = (query) =>
      venues
        .filter((venue) => normalize([venue.name, venue.city].filter(Boolean).join(' ')).includes(query))
        .sort(compareName);

    const renderSearch = () => {
      const rawQuery = search.value.trim();
      const query = normalize(rawQuery);
      searchClear.hidden = rawQuery.length === 0;
      if (startExtras) startExtras.hidden = query.length >= 2;

      if (query.length < 2) {
        searchResults.hidden = true;
        cityGroup.hidden = true;
        venueGroup.hidden = true;
        citySearchMore.hidden = true;
        venueSearchMore.hidden = true;
        searchStatus.textContent = 'Enter at least 2 letters';
        return;
      }

      const cities = matchingCities(query);
      const venueMatches = matchingVenues(query);
      const visibleCities = cities.slice(0, searchCityLimit);
      const visibleVenues = venueMatches.slice(0, searchVenueLimit);

      cityResults.replaceChildren(...visibleCities.map(makeCityButton));
      venueResults.replaceChildren(...visibleVenues.map(makeVenueButton));
      cityGroup.hidden = cities.length === 0;
      venueGroup.hidden = venueMatches.length === 0;
      citySearchMore.hidden = cities.length <= visibleCities.length;
      venueSearchMore.hidden = venueMatches.length <= visibleVenues.length;
      searchResults.hidden = cities.length === 0 && venueMatches.length === 0;
      searchStatus.textContent =
        cities.length || venueMatches.length
          ? cities.length + ' ' + (cities.length === 1 ? 'city' : 'cities') + ' and ' + venueMatches.length + ' ' + (venueMatches.length === 1 ? 'venue' : 'venues') + ' found.'
          : 'No matching cities or venues found.';
    };

    const searchAll = async () => {
      const query = normalize(search.value);
      if (query.length < 2) {
        renderSearch();
        return;
      }

      searchStatus.textContent = 'Searching venues...';
      root.setAttribute('aria-busy', 'true');
      try {
        await ensureIndex();
        renderSearch();
      } catch (error) {
        searchStatus.textContent = 'The complete directory could not be loaded. Try again.';
      } finally {
        root.removeAttribute('aria-busy');
      }
    };

    const renderCity = () => {
      if (!selectedCity) return;
      const query = normalize(citySearch.value);
      const matches = venues
        .filter((venue) => cityKey(venue) === selectedCity.key)
        .filter((venue) => !query || normalize(venue.name).includes(query))
        .sort(compareName);

      cityClear.hidden = citySearch.value.length === 0;
      cityList.replaceChildren();

      let previousLetter = '';
      matches.forEach((venue) => {
        const letter = String(venue.name || '#').charAt(0).toUpperCase();
        if (letter !== previousLetter) {
          const marker = document.createElement('p');
          marker.className = 'venue-discovery__letter';
          marker.textContent = letter;
          marker.setAttribute('aria-hidden', 'true');
          cityList.appendChild(marker);
          previousLetter = letter;
        }
        cityList.appendChild(makeVenueButton(venue));
      });

      cityStatus.textContent =
        matches.length === 0
          ? 'No venues match this search.'
          : matches.length + ' ' + (matches.length === 1 ? 'venue' : 'venues') + ' in ' + selectedCity.city + '.';
    };

    const selectCity = async (key) => {
      await ensureIndex();
      const match = venues.find((venue) => cityKey(venue) === key);
      if (!match) return;
      selectedCity = {
        key,
        city: match.city,
        state: match.state,
        country: match.country
      };
      citySearch.value = '';
      selectedCityName.textContent = selectedCity.city;
      renderCity();
      showView('city');
      root.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    const selectPopularCity = async (cityName) => {
      root.setAttribute('aria-busy', 'true');
      try {
        await ensureIndex();
        const cityMatch = venues.find((venue) => normalize(venue.city) === normalize(cityName));
        if (!cityMatch) {
          search.value = cityName;
          await searchAll();
          showView('start');
          return;
        }
        await selectCity(cityKey(cityMatch));
      } catch (error) {
        searchStatus.textContent = 'The complete directory could not be loaded. Try again.';
      } finally {
        root.removeAttribute('aria-busy');
      }
    };

    const renderBrowse = () => {
      const sorted = venues.slice().sort(compareName);
      const totalPages = Math.max(1, Math.ceil(sorted.length / browsePageSize));
      browsePage = Math.min(Math.max(1, browsePage), totalPages);
      const start = (browsePage - 1) * browsePageSize;
      const visible = sorted.slice(start, start + browsePageSize);

      browseList.replaceChildren();
      let previousLetter = '';
      visible.forEach((venue) => {
        const letter = String(venue.name || '#').charAt(0).toUpperCase();
        if (letter !== previousLetter) {
          const marker = document.createElement('p');
          marker.className = 'venue-discovery__letter';
          marker.textContent = letter;
          marker.setAttribute('aria-hidden', 'true');
          browseList.appendChild(marker);
          previousLetter = letter;
        }
        browseList.appendChild(makeVenueButton(venue));
      });

      const firstVisible = sorted.length ? start + 1 : 0;
      const lastVisible = Math.min(start + visible.length, sorted.length);
      browseStatus.textContent = 'Showing ' + firstVisible + '–' + lastVisible + ' of ' + sorted.length + ' venues.';
      browsePageStatus.textContent = 'Page ' + browsePage + ' of ' + totalPages;
      browsePrevious.disabled = browsePage <= 1;
      browseNext.disabled = browsePage >= totalPages;
      browsePagination.hidden = totalPages <= 1;
    };

    const openBrowse = async () => {
      browseStatus.textContent = 'Loading the venue directory...';
      showView('browse');
      root.setAttribute('aria-busy', 'true');
      try {
        await ensureIndex();
        browsePage = 1;
        renderBrowse();
      } catch (error) {
        browseStatus.textContent = 'The complete directory could not be loaded. Try again.';
      } finally {
        root.removeAttribute('aria-busy');
      }
      root.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    const detailBlock = (number, title, html, options = {}) => {
      if (!html && !options.link) return '';
      const content = html ? '<div class="venue-discovery__answer rte">' + html + '</div>' : '';
      const externalLink = options.link
        ? '<a class="venue-discovery__inline-link" href="' + escapeHtml(options.link) + '" target="_blank" rel="noopener noreferrer external">' + escapeHtml(options.linkLabel) + '</a>'
        : '';
      return (
        '<details class="venue-discovery__question"' + (options.open ? ' open' : '') + '>' +
          '<summary><span>' + String(number).padStart(2, '0') + '</span><strong>' + escapeHtml(title) + '</strong></summary>' +
          '<div class="venue-discovery__question-body">' + content + externalLink + '</div>' +
        '</details>'
      );
    };

    const renderVenue = (venue, details) => {
      const location = [venue.city, venue.state].filter(Boolean).join(', ');
      const venueHeadingId = escapeHtml(root.id.replace('VenueDiscovery-', 'VenueSelectedHeading-'));
      const venueLocation = location ? '<p class="venue-discovery__selected-location">' + escapeHtml(location) + '</p>' : '';
      const venueLink = '<a class="venue-discovery__full-guide" href="' + escapeHtml(venue.url) + '"><span>Read more about this venue</span><span aria-hidden="true">›</span></a>';

      if (normalize(venue.status).includes('closed')) {
        selectedVenueContent.innerHTML =
          '<article class="venue-discovery__selected venue-discovery__selected--historic">' +
            '<h2 id="' + venueHeadingId + '">' + escapeHtml(venue.name) + '</h2>' +
            venueLocation +
            '<p class="venue-discovery__historic-status">Closed / historic venue</p>' +
            venueLink +
          '</article>';
        return;
      }

      const questions = [
        detailBlock(1, 'How do I get there?', details.gettingThere, {
          link: details.directionsUrl,
          linkLabel: 'Open map'
        }),
        detailBlock(2, 'Can I bring water?', details.waterPolicy),
        detailBlock(3, 'Bag policy', details.condensedBagPolicy),
        detailBlock(4, 'Local tips', details.localTips)
      ].filter(Boolean).join('');

      selectedVenueContent.innerHTML =
        '<article class="venue-discovery__selected">' +
          '<h2 id="' + venueHeadingId + '">' + escapeHtml(venue.name) + '</h2>' +
          venueLocation +
          (questions
            ? '<div class="venue-discovery__questions">' +
                '<h3>Your show questions.</h3>' +
                '<p>Tap a question to see the answer.</p>' +
                questions +
              '</div>'
            : '') +
          venueLink +
        '</article>';
    };

    const selectVenue = async (handle) => {
      const venue = venues.find((item) => item.handle === handle);
      if (!venue) return;

      selectedVenue = venue;
      const requestId = ++detailRequest;
      backCityName.textContent =
        venueReturnView === 'browse'
          ? 'all venues'
          : venueReturnView === 'city' && selectedCity
            ? selectedCity.city
            : 'search';
      selectedVenueContent.innerHTML = '<p class="venue-discovery__loading">Loading ' + escapeHtml(venue.name) + '...</p>';
      showView('venue');
      root.scrollIntoView({ block: 'start', behavior: 'smooth' });

      try {
        const response = await fetch(preservePreviewParams(venue.url), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('Venue details could not be loaded.');
        const venueDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payload = venueDocument.querySelector('[data-venue-discovery-details]');
        if (!payload) throw new Error('Venue details are unavailable.');
        const details = JSON.parse(payload.textContent);
        if (requestId !== detailRequest) return;
        renderVenue(venue, details);
      } catch (error) {
        if (requestId !== detailRequest) return;
        selectedVenueContent.innerHTML =
          '<div class="venue-discovery__error"><h2>' + escapeHtml(venue.name) + '</h2>' +
          '<p>Practical details could not be loaded here.</p>' +
          '<a class="venue-discovery__full-guide" href="' + escapeHtml(venue.url) + '"><span>Open the full venue guide</span><span aria-hidden="true">›</span></a></div>';
      }
    };

    root.addEventListener('click', (event) => {
      const cityButton = event.target.closest('[data-select-city]');
      if (cityButton) {
        selectCity(cityButton.dataset.selectCity);
        return;
      }

      const venueButton = event.target.closest('[data-select-venue]');
      if (venueButton) {
        const sourceView = venueButton.closest('[data-venue-view]')?.dataset.venueView;
        venueReturnView = sourceView === 'browse' ? 'browse' : sourceView === 'city' ? 'city' : 'start';
        selectVenue(venueButton.dataset.selectVenue);
        return;
      }

      const popularCityButton = event.target.closest('[data-popular-city]');
      if (popularCityButton) {
        selectPopularCity(popularCityButton.dataset.popularCity);
        return;
      }

      if (event.target.closest('[data-browse-all-venues]')) {
        openBrowse();
        return;
      }

      if (event.target.closest('[data-browse-back]')) {
        showView('start');
        search.focus();
        return;
      }

      if (event.target.closest('[data-browse-previous]')) {
        browsePage -= 1;
        renderBrowse();
        root.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }

      if (event.target.closest('[data-browse-next]')) {
        browsePage += 1;
        renderBrowse();
        root.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }

      if (event.target.closest('[data-venue-change-city]')) {
        selectedCity = null;
        selectedVenue = null;
        showView('start');
        search.focus();
        return;
      }

      if (event.target.closest('[data-venue-back-city]')) {
        if (venueReturnView === 'browse') {
          renderBrowse();
          showView('browse');
        } else if (venueReturnView === 'city' && selectedCity) {
          renderCity();
          showView('city');
        } else {
          showView('start');
        }
        return;
      }

      if (event.target.closest('[data-venue-reset]')) {
        selectedCity = null;
        selectedVenue = null;
        search.value = '';
        searchCityLimit = 6;
        searchVenueLimit = 8;
        venueReturnView = 'start';
        renderSearch();
        showView('start');
        search.focus();
        return;
      }

      if (event.target.closest('[data-venue-search-clear]')) {
        search.value = '';
        searchCityLimit = 6;
        searchVenueLimit = 8;
        renderSearch();
        search.focus();
        return;
      }

      if (event.target.closest('[data-city-search-more]')) {
        searchCityLimit += 6;
        renderSearch();
        return;
      }

      if (event.target.closest('[data-venue-search-more]')) {
        searchVenueLimit += 8;
        renderSearch();
        return;
      }

      if (event.target.closest('[data-venue-city-clear]')) {
        citySearch.value = '';
        renderCity();
        citySearch.focus();
      }
    });

    search.addEventListener('input', () => {
      searchCityLimit = 6;
      searchVenueLimit = 8;
      window.clearTimeout(inputTimer);
      inputTimer = window.setTimeout(searchAll, 180);
    });

    citySearch.addEventListener('input', renderCity);

    showView('start');
  };

  document.querySelectorAll('[id^="VenueDiscovery-"]').forEach(initDiscovery);
})();
